import { createSelector } from '@reduxjs/toolkit';
import { mapLocationsByDetail, mapLogisticsByDetail } from 'gql/service/graph/activityService';
import { Logistic } from 'gql/autogen/Logistic';
import { Location } from 'gql/autogen/Location';
import { DepartureCardProps } from 'component/page/detail/MoreInfoSections/DepartureAndReturn/Departure';
import { selectActivityContentLocale, selectActivityDetail, selectExpandedAccordionFromDetailState } from 'component/page/detail/store/detail.selectors';
import { DetailContentOnToggleProps } from 'component/page/detail/DetailPageContent/DetailPageContent.selectors';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { selectCmsFromBootstrap, selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { ExpandedAccordionDict } from 'core/type/Details.types';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

interface StateProps {
    cms: CmsValue;
    logistic: Logistic;
    departurePoints: DepartureCardProps[];
    returnPoints: DepartureCardProps[];
    expandedAccordion: ExpandedAccordionDict;
    isContentReadEnabled: boolean;
}

export type DepartureAndReturnProps = StateProps & DetailContentOnToggleProps;
export type DepartureAndReturnModularProps = StateProps;

const mapDepartureAndReturnList = (logistics: Logistic, loc: Location[], isDeparturePoints: boolean, contentLocale: string): DepartureCardProps[] => {
    const locationPoints = (isDeparturePoints ? logistics.startEnd && logistics.startEnd.startLocation : logistics.startEnd && logistics.startEnd.endLocation) || [];

    const departureList: DepartureCardProps[] = locationPoints
        .map((point) => {
            return {
                locationDescription: loc.find((x) => x.id === point.locationRefId)?.address?.addressLine || '',
                description: point.description || '',
                id: locationPoints.indexOf(point),
                contentLocale,
            };
        })
        .filter((point) => point.description || point.locationDescription)
        .splice(0, 5);

    return departureList;
};

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectActivityDetail,
    selectActivityContentLocale,
    selectExpandedAccordionFromDetailState,
    selectExperimentsFromBootstrap,
    (cms, detail, contentLocale, expandedAccordion, experiments) => {
        const logistic = mapLogisticsByDetail(detail);
        const locations = mapLocationsByDetail(detail);
        const departurePoints = mapDepartureAndReturnList(logistic, locations, true, contentLocale);
        const returnPoints = mapDepartureAndReturnList(logistic, locations, false, contentLocale);
        const isContentReadEnabled = isB(ExperimentId.ABE_4553, experiments);

        return { cms, logistic, departurePoints, returnPoints, contentLocale, expandedAccordion, isContentReadEnabled };
    },
);
